import type { TFunction } from 'i18next';

/**
 * SKALA POKOI W WYSZUKIWARCE: „Kawalerka · 2 · 3 · 4+" (zadanie R, 28.09).
 *
 * Wartości w filtrze i w adresie zostają LICZBAMI (1, 2, 3, 4 = „4+"), więc stare linki z
 * `?rooms=1,2` działają bez zmian — zmienia się wyłącznie to, jak je podpisujemy. Jedynka to
 * „Kawalerka": tak ludzie szukają i tak piszą ogłoszenia; „1 pokój" brzmiało jak pokój do
 * wynajęcia, a tych w wynikach nie ma (SERP pokazuje tylko całe mieszkania).
 *
 * Jedna funkcja etykiety dla KAŻDEGO miejsca, które pokazuje wybór: pastylka na telefonie,
 * pastylka „Pokoje" na pulpicie, pastylka w rzędzie licznika. Wcześniej były trzy sklejki
 * „2, 3 pok." w trzech miejscach, a telefon nie pokazywał wyboru wcale.
 */
export const MAKS_POKOI = 4; // przycisk „4+"

/**
 * ZBIÓR POKOI Z WIERSZA `search_requirements_public` (widok nad zapisanym zestawem), jedna reguła dla wyszukiwarki,
 * onboardingu, podsumowań i dopasowania właściciela (30.09):
 *  - `rooms` (zbiór) wygrywa — 1:1, posortowany, tylko skala 1..4+;
 *  - stary wiersz z samym `min_rooms`: 1 = sama kawalerka (29.09), N = N..4+;
 *  - nic → null (dowolna liczba pokoi).
 */
export function pokojeZProfilu(
  rooms: readonly number[] | null | undefined,
  minRooms: number | null | undefined,
): number[] | null {
  const zbior = [...new Set(rooms ?? [])].filter((r) => r >= 1 && r <= MAKS_POKOI).sort((a, b) => a - b);
  if (zbior.length) return zbior;
  if (typeof minRooms === 'number' && minRooms >= 1) {
    const od = Math.min(minRooms, MAKS_POKOI);
    return od === 1 ? [1] : Array.from({ length: MAKS_POKOI - od + 1 }, (_, i) => od + i);
  }
  return null;
}

/** Czy oferta z `pokojeOferty` pasuje do zbioru (4 = „4 lub więcej"). Brak zbioru = pasuje. */
export function pokojePasuja(zbior: readonly number[] | null, pokojeOferty: number): boolean {
  if (!zbior?.length) return true;
  return zbior.includes(Math.min(pokojeOferty, MAKS_POKOI));
}

/**
 * Podpis wyboru z filtra:
 *  - nic → null (przycisk mówi wtedy samo „Pokoje");
 *  - jedna wartość → „Kawalerka" / „2 pokoje" / „4+ pokoje";
 *  - ciąg od N do 4+ (co najmniej dwie wartości) → „N+ pokoje";
 *  - reszta → wyliczenie: „2, 4+ pokoje", „2, 3 pokoje";
 *  - kawalerka razem z innymi → „Kawalerka, 2+ pokoje".
 */
export function etykietaWyboruPokoi(rooms: readonly number[] | undefined, t: TFunction): string | null {
  if (!rooms?.length) return null;
  const wybrane = [...new Set(rooms)].sort((a, b) => a - b);
  const kawalerka = wybrane[0] === 1;
  const reszta = kawalerka ? wybrane.slice(1) : wybrane;
  const studio = t('search.filters.rooms_studio');
  if (reszta.length === 0) return studio;

  const od = reszta[0];
  const ciagDoKonca =
    reszta.length >= 2 &&
    reszta[reszta.length - 1] === MAKS_POKOI &&
    reszta.every((r, i) => r === od + i);
  const lista = ciagDoKonca
    ? `${od}+`
    : reszta.map((r) => (r >= MAKS_POKOI ? `${MAKS_POKOI}+` : String(r))).join(', ');
  const pokoje = t('search.filters.rooms_selection', { lista });
  return kawalerka ? `${studio}, ${pokoje}` : pokoje;
}

/**
 * WARIANT KRÓTKI — skrót „Pokoje" na telefonie (decyzja Piotra 28.09: pastylka nie może być
 * szersza niż pastylka ceny „do 4 000 zł"):
 *  - tylko kawalerka → „Kawalerka";
 *  - jedna liczba → „2 pok."; ciąg od N do 4+ → „2+ pok.";
 *  - każdy inny wybór (kawalerka z liczbą, przerwa, dwie liczby bez 4+) → „Pokoje" i kółko
 *    z liczbą wybranych wartości, jak znacznik przy „Filtry". Pełną listę widać w arkuszu.
 * Zwraca null bez wyboru.
 */
export function etykietaWyboruPokoiKrotka(
  rooms: readonly number[] | undefined,
  t: TFunction,
): { tekst: string; znacznik: number | null } | null {
  if (!rooms?.length) return null;
  const wybrane = [...new Set(rooms)].sort((a, b) => a - b);
  if (wybrane.length === 1 && wybrane[0] === 1) return { tekst: t('search.filters.rooms_studio'), znacznik: null };
  if (wybrane[0] !== 1) {
    const od = wybrane[0];
    if (wybrane.length === 1) {
      const lista = od >= MAKS_POKOI ? `${MAKS_POKOI}+` : String(od);
      return { tekst: t('search.filters.rooms_selection_short', { lista }), znacznik: null };
    }
    const ciagDoKonca =
      wybrane[wybrane.length - 1] === MAKS_POKOI && wybrane.every((r, i) => r === od + i);
    if (ciagDoKonca) return { tekst: t('search.filters.rooms_selection_short', { lista: `${od}+` }), znacznik: null };
  }
  return { tekst: t('search.filters.rooms'), znacznik: wybrane.length };
}

/** Podpis liczby pokoi JEDNEJ oferty: 1 → „Kawalerka", reszta „2 pokoje", „5 pokoi". */
export function etykietaPokoiOferty(rooms: number | null | undefined, t: TFunction): string | null {
  if (rooms == null) return null;
  return rooms === 1 ? t('search.filters.rooms_studio') : t('search.card.rooms', { count: rooms });
}
